import _ from 'lodash'
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'

type SaveFunction = (editCopy: object) => object

@Component
export default class EditorMixin extends Vue {
  @Prop({ default: () => ({}), type: Object })
  public defaults!: object

  @Prop({ type: Object })
  public value!: object | null

  public concurrentUpdates: object[] = []
  public updatesWhileSaving: object[] = []
  public editCopy: object | null = null
  public commonBase: object | null = null

  public makingCopy: boolean = false
  public saving: boolean = false

  get clean(): boolean {
    return _.isEqual(this.value, this.commonBase)
  }

  get modified(): boolean {
    return !_.isEqual(this.editCopy, this.commonBase)
  }

  get stale(): boolean {
    return this.concurrentUpdates.length > 0
  }

  get slotProperties(): object {
    const self: any = this
    return {
      clean: this.clean,
      conflicts: this.concurrentUpdates,
      copy: this.editCopy,
      modified: this.modified,
      save: this.save,
      saving: this.saving,
      stale: this.stale,
    }
  }

  public created() {
    this.makeEditCopy()
  }

  public makeEditCopy() {
    this.makingCopy = true
    if (this.value === null) {
      this.commonBase = null
      this.editCopy = null
    } else {
      this.commonBase = _.cloneDeep(this.value)
      this.editCopy = _.defaultsDeep(_.cloneDeep(this.value), this.defaults)
    }
    this.concurrentUpdates = []
    this.updatesWhileSaving = []
    this.makingCopy = false
  }

  public save(update: object | null = null): PromiseLike<object> {
    this.saving = true
    if (update) {
      _.merge(this.editCopy, update)
    }
    if (this.editCopy === null) {
      throw new Error('editCopy is null')
    }
    return Promise.resolve(this.saveFunction(this.editCopy)).then(
      savedState => {
        this.commonBase = _.cloneDeep(savedState)
        this.saving = false

        const handOut = _.cloneDeep(savedState)
        this.$emit('saved', handOut)
        return handOut
      },
      error => {
        this.saving = false
        throw error
      }
    )
  }

  public saveFunction(editCopy: object): object {
    return editCopy
  }

  @Watch('value', { deep: true })
  public onValueChanged(val: object, oldVal: object) {
    if (this.modified) {
      const concurrentUpdate = _.cloneDeep(val)
      if (this.saving) {
        this.updatesWhileSaving.push(concurrentUpdate)
      } else {
        this.concurrentUpdates.push(concurrentUpdate)
      }
    } else {
      this.makeEditCopy()
    }
  }

  @Watch('editCopy', { deep: true })
  public onEditCopyChanged(val: object, oldVal: object) {
    if (!this.makingCopy) {
      this.$emit('input', this.editCopy)
    }
  }

  @Watch('saving')
  public afterSavingCompleted(nowIs: boolean, was: boolean) {
    if (nowIs === false && was === true) {
      this.updatesWhileSaving
        .filter(update => !_.isEqual(update, this.commonBase))
        .forEach(update => this.concurrentUpdates.push(update))
      this.updatesWhileSaving = []
    }
  }
}
